
import { Component, Vue } from "vue-property-decorator";
import TechAppUsersList from "./TechAppUsersList.vue";
import EditEmailModal from "./EditEmailModal.vue";
import { TechAppUser } from "@/store/modules";
import { userManagementService } from "@/services/UserManagementService";

@Component({
  components: { TechAppUsersList, EditEmailModal },
})
export default class TechAppUsersListHandler extends Vue {
  actionLoading = false;
  emailUpdateLoading = false;
  showEditEmailModal = false;
  showEmailSuccessBanner = false;
  selectedUserEmail = '';
  currentPage = 1;
  colFilter = {};
  orderedItems: any[] = [];
  sorterValue = { column: null as string | null, asc: true };
  itemsPerPage = 25;

  mounted() {
    this.load();
  }

  load() {
    this.$nextTick(() => this.fetchRender());
  }

  async fetchRender() {
    const params: any = {
      offset: (this.currentPage - 1) * this.itemsPerPage, // Calculate offset from page number
      limit: this.itemsPerPage
    };

    // Add sorting parameters
    if (this.sorterValue && this.sorterValue.column) {
      params.sort = this.sorterValue.column;
      params.order = this.sorterValue.asc ? 'asc' : 'desc';
    }

    // Group filter values as arrays with filter[fieldname] format
    if (this.colFilter && Object.keys(this.colFilter).length > 0) {
      Object.keys(this.colFilter).forEach(key => {
        const value = (this.colFilter as any)[key];
        if (value && value.trim() !== '') {
          params[`filter[${key}]`] = value;
        }
      });
    }

    await TechAppUser.dispatch("all", { params });
  }

  get items(): Array<any> {
    // Use the ordered items from the API response to preserve order
    const orderedItems = TechAppUser.store().state.entities.techAppUser.orderedItems;
    if (orderedItems && orderedItems.length > 0) {
      return orderedItems;
    }
    // Fallback to query if orderedItems is not available
    return TechAppUser.query().withAll().get();
  }

  get loading(): boolean {
    return !!TechAppUser.store().state.entities.techAppUser.fetching;
  }

  get total() {
    return TechAppUser.store().state.entities.techAppUser.total;
  }

  get numPages(): number {
    const total = this.total;
    if (!total) {
      return 0;
    }
    return Math.max(Math.ceil(total / this.itemsPerPage), 1);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.load();
  }

  handleFilterChange(filterValue: any) {
    this.colFilter = filterValue;
    this.currentPage = 1; // Reset to first page when filtering
    this.load();
  }

  handleSorterChange(sorterValue: { column: string; asc: boolean }) {
    this.sorterValue = sorterValue;
    this.currentPage = 1; // Reset to first page when sorting
    this.load();
  }

  handleItemsPerPageChange(itemsPerPage: number) {
    this.itemsPerPage = itemsPerPage;
    this.currentPage = 1; // Reset to first page when changing items per page
    this.load();
  }

  async handleToggleUserAccess(payload: { cognito_email: string; action: string }) {
    this.actionLoading = true;
    
    try {
      if (payload.action === 'enable') {
        await userManagementService.enableUser(payload.cognito_email);
        this.$root.$bvToast.toast('User access enabled successfully', {
          title: 'Success',
          variant: 'success'
        });
      } else if (payload.action === 'disable') {
        await userManagementService.disableUser(payload.cognito_email);
        this.$root.$bvToast.toast('User access disabled successfully', {
          title: 'Success',
          variant: 'success'
        });
      }
      
      // Reload the data to reflect the changes
      await this.fetchRender();
      
    } catch (error) {
      console.error('Error toggling user access:', error);
      this.$root.$bvToast.toast('Failed to update user access. Please try again.', {
        title: 'Error',
        variant: 'danger'
      });
    } finally {
      this.actionLoading = false;
    }
  }

  handleEditUserEmail(payload: { cognito_email: string; emp_id: number }) {
    this.selectedUserEmail = payload.cognito_email;
    this.showEmailSuccessBanner = false;
    this.showEditEmailModal = true;
  }

  handleCancelEditEmail() {
    this.showEditEmailModal = false;
    this.showEmailSuccessBanner = false;
    this.selectedUserEmail = '';
  }

  async handleSaveEmail(payload: { currentEmail: string; newEmail: string }) {
    this.emailUpdateLoading = true;
    
    try {
      const response = await userManagementService.updateEmail(payload.currentEmail, payload.newEmail);
      
      // Check if the response indicates success
      if (response.data && response.data.status === 'success') {
        this.$root.$bvToast.toast(response.data.message || 'User email updated successfully', {
          title: 'Success',
          variant: 'success'
        });
        
        // Show success banner in modal
        this.showEmailSuccessBanner = true;
        
        // Close modal and reload data after a short delay to show success banner
        setTimeout(async () => {
          this.showEditEmailModal = false;
          this.showEmailSuccessBanner = false;
          this.selectedUserEmail = '';
          await this.fetchRender();
        }, 2000);
      } else {
        // Handle API error response - modal stays open
        const errorMessage = response.data?.message || 'Failed to update user email';
        this.$root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger'
        });
        // Modal remains open to show error
      }
      
    } catch (error) {
      console.error('Error updating user email:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to update user email. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const responseData = error.response.data;
        
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        } else if (status === 409) {
          errorMessage = 'An account with this email already exists';
        } else if (status === 400) {
          errorMessage = 'Invalid email format or request';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      this.$root.$bvToast.toast(errorMessage, {
        title: 'Error',
        variant: 'danger'
      });
      // Modal remains open to show error
    } finally {
      this.emailUpdateLoading = false;
    }
  }
}
